/**
 * FeatureForm - Manages a list of features with drag-and-drop reordering.
 *
 * Uses FeatureRow molecules for individual feature items (edit mode)
 * and an always-visible FeatureRow (create mode) for adding new features.
 */

import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { FeatureRow } from '@/components/molecules';
import type { SimulationFormValues } from '@/types/api';
import { postFeatureMove } from '@/lib/api';
import { anchorAbove } from '@/lib/ordering';
import { useErrorHandler } from '@/hooks';
import { useSyncedFieldArray } from './syncedFieldArray';
import { useWriteErrors } from '@/hooks/useWriteErrors';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';

export interface FeatureFormHandle {
  featureCount: number;
}

interface FeatureFormProps {
  simulationId?: string | null;
  /** Reports the current feature count up to the parent (for rendering summaries reactively). */
  onFeatureCountChange?: (count: number) => void;
}

export const FeatureForm = forwardRef<FeatureFormHandle, FeatureFormProps>(function FeatureForm(
  { simulationId, onFeatureCountChange },
  ref,
) {
  const { handleError } = useErrorHandler('Features');
  const { onMoveError } = useWriteErrors('feature', handleError);
  const { control, getValues } = useFormContext<SimulationFormValues>();

  const { fields, remove, move, drag } = useSyncedFieldArray({ control, name: 'features' });

  const features = useWatch({ control, name: 'features' });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    const oldIndex = fields.findIndex((f) => f.id === active.id);
    const newIndex = over ? fields.findIndex((f) => f.id === over.id) : -1;
    if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) {
      drag.ended(false);
      return;
    }

    move(oldIndex, newIndex);
    drag.ended(true);

    // Send the move itself, not this screen's whole list: the server applies
    // it to the current order, so another user's concurrent move survives.
    const ids = (getValues('features') || []).map((f) => f.id);
    const featureId = ids[newIndex];
    if (simulationId && featureId) {
      try {
        await postFeatureMove(simulationId, featureId, anchorAbove(ids, newIndex));
      } catch (error) {
        onMoveError(error);
      }
    }
  };

  useImperativeHandle(
    ref,
    () => ({
      featureCount: fields.length,
    }),
    [fields.length],
  );

  // Report the count up so the parent can render summaries reactively
  // (reading the ref's current value during render is not reactive).
  useEffect(() => {
    onFeatureCountChange?.(fields.length);
  }, [fields.length, onFeatureCountChange]);

  return (
    <div className="space-y-2">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={drag.started}
        onDragCancel={() => drag.ended(false)}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={fields.map((f) => f.id)} strategy={verticalListSortingStrategy}>
          {fields.map((field, index) => {
            const featureId = features?.[index]?.id;
            // Only render FeatureRow for saved features (with an ID)
            if (!featureId) return null;
            return (
              <FeatureRow
                key={field.id}
                mode="edit"
                id={field.id}
                index={index}
                featureId={featureId}
                onRemove={() => remove(index)}
                canRemove={true}
                simulationId={simulationId || null}
              />
            );
          })}
        </SortableContext>
      </DndContext>

      {/* Always-visible creation row at the bottom */}
      {simulationId && <FeatureRow mode="create" simulationId={simulationId} />}
    </div>
  );
});
